import { useFormikWithDirtyForm } from "@shared";
import { useConfirmationDialogContext } from "@shared";
import { useAlertContext } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { endOfDay } from "date-fns";

import { AssignmentResponse, AssignmentStatus, UpdateAssignmentRequest, assignmentsApi } from "@/api";
import { AssignmentFormValues } from "@/common/components/assignments/shared/AssignmentForm.types";
import { useAssignmentFormOptions } from "@/common/components/assignments/shared/hooks/useAssignmentFormOptions";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

export function useAssignmentEdit(assignment: AssignmentResponse, onClose: () => void) {
  const { t } = useTranslation();
  const { showSuccess } = useAlertContext();
  const { showConfirmationDialog } = useConfirmationDialogContext();
  const queryClient = useQueryClient();
  const { priorityOptions, validationSchema } = useAssignmentFormOptions();

  const updateAssignmentMutation = useMutation({
    mutationFn: (request: UpdateAssignmentRequest) =>
      assignmentsApi.updateAssignment({
        assignmentId: assignment.assignmentId,
        updateAssignmentRequest: request,
      }),

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Assignments] });
      showSuccess(t("assignments.editDialog.successfullyUpdated"));
      onClose();
    },
  });

  const deleteAssignmentMutation = useMutation({
    mutationFn: () =>
      assignmentsApi.deleteAssignment({
        assignmentId: assignment.assignmentId,
      }),

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Assignments] });
      showSuccess(t("assignments.editDialog.successfullyDeleted"));
      onClose();
    },
  });

  const toggleAssignmentStatusMutation = useMutation({
    mutationFn: () =>
      assignmentsApi.updateAssignmentStatus({
        assignmentId: assignment.assignmentId,
        updateAssignmentStatusRequest: {
          status: assignment.status === AssignmentStatus.Open ? AssignmentStatus.Completed : AssignmentStatus.Open,
        },
      }),

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Assignments] });
      showSuccess(
        assignment.status === AssignmentStatus.Open
          ? t("assignments.editDialog.successfullyClosed")
          : t("assignments.editDialog.successfullyReopened")
      );
      onClose();
    },
  });

  const handleToggleStatus = () => {
    toggleAssignmentStatusMutation.mutate();
  };

  const handleDelete = () => {
    showConfirmationDialog({
      dialogTitleText: t("assignments.editDialog.deleteConfirmTitle"),
      dialogText: t("assignments.editDialog.deleteConfirmText"),
      agreeText: t("common.yes"),
      discardText: t("common.no"),
      onClose: (confirmed) => {
        if (confirmed) {
          deleteAssignmentMutation.mutate();
        }
      },
    });
  };

  const formik = useFormikWithDirtyForm<AssignmentFormValues>({
    initialValues: {
      dueDate: new Date(assignment.dueDate),
      description: assignment.description,
      priority: assignment.priority,
      assignedCounselorId: assignment.assignedCounselorId,
      assignedClientId: assignment.assignedClientId ?? null,
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values) => {
      const request: UpdateAssignmentRequest = {
        dueDate: endOfDay(values.dueDate),
        description: values.description,
        priority: values.priority,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        assignedCounselorId: values.assignedCounselorId!,
      };

      try {
        await updateAssignmentMutation.mutateAsync(request);
      } catch {
        // Reported by the global error handling; the dialog stays open so the input is kept.
      }
    },
  });

  return {
    formik,
    priorityOptions,
    handleDelete,
    isDeleting: deleteAssignmentMutation.isPending,
    handleToggleStatus,
    isTogglingStatus: toggleAssignmentStatusMutation.isPending,
  };
}
